import { type HTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

export type AlertVariant = 'info' | 'success' | 'warning' | 'danger';

// `title` is omitted from the DOM attributes and re-declared: the native
// `title` is the tooltip string, while an Alert's title is rendered content.
export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: AlertVariant;
  /** The alert's heading. Rendered as content, NOT as the HTML tooltip attribute. */
  title?: ReactNode;
}

const variantClasses: Record<AlertVariant, string> = {
  info: 'bg-surface border-border text-foreground',
  success: 'bg-success/10 border-success/30 text-success',
  warning: 'bg-warning/10 border-warning/40 text-warning',
  danger: 'bg-danger/10 border-danger/40 text-danger',
};

export function Alert({
  variant = 'info',
  title,
  children,
  className,
  ...rest
}: AlertProps): JSX.Element {
  return (
    <div
      role="alert"
      className={cn(
        'rounded-md border px-4 py-3 text-sm',
        variantClasses[variant],
        className,
      )}
      {...rest}
    >
      {title && <p className="font-medium">{title}</p>}
      {children && <div className={cn(title && 'mt-1')}>{children}</div>}
    </div>
  );
}
